import { useState } from 'react';
import { useOutletContext } from 'react-router';
import { useQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import type { ColDef } from 'ag-grid-community';
import { graphql } from '../../../gql';
import type { AssetEquipmentLogQuery } from '../../../gql/graphql';
import { DataGrid } from '../../../components/data-grid';
import { DateInput, LoadingOverlay, displayDate, displayText } from '../../../components/ui';
import type { AssetDetailFragment } from '../AssetDetailPage';

const ASSET_EQUIPMENT_LOG_QUERY = graphql(`
  query AssetEquipmentLog($assetId: Int!, $fromDate: LocalDate!, $toDate: LocalDate!, $includeAllClockReadings: Boolean!) {
    assetEquipmentLog(assetId: $assetId, fromDate: $fromDate, toDate: $toDate, includeAllClockReadings: $includeAllClockReadings) {
      date eventType details
    }
  }
`);

type LogRow = NonNullable<AssetEquipmentLogQuery['assetEquipmentLog']>[number];

const columns: ColDef<LogRow>[] = [
  { field: 'date', headerName: 'Date', flex: 1, sort: 'desc', valueFormatter: (params) => displayDate(params.value as string) },
  { field: 'eventType', headerName: 'Event Type', flex: 2, valueFormatter: (params) => displayText(params.value) },
  { field: 'details', headerName: 'Details', flex: 4, wrapText: true, autoHeight: true, valueFormatter: (params) => displayText(params.value) },
];

function localDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function defaultRange() {
  const today = new Date();
  const lastMonth = new Date(today);
  lastMonth.setDate(1);
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  lastMonth.setDate(Math.min(today.getDate(), new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0).getDate()));
  return { fromDate: localDate(lastMonth), toDate: localDate(today), includeAllClockReadings: false };
}

/** Read-only Equipment Log for an Asset authorized by the parent route. */
export function AssetEquipmentLogTab() {
  const asset = useOutletContext<AssetDetailFragment | null>();
  const [draft, setDraft] = useState(defaultRange);
  const [applied, setApplied] = useState<typeof draft | null>(null);
  const invalidRange = draft.fromDate > draft.toDate;
  const { data, loading, error, refetch } = useQuery(ASSET_EQUIPMENT_LOG_QUERY, {
    variables: { assetId: asset?.id ?? 0, ...applied ?? draft },
    skip: asset == null || applied == null,
  });

  const clear = () => {
    setDraft(defaultRange());
    setApplied(null);
  };
  const rows = applied && !error ? data?.assetEquipmentLog : null;

  return (
    <section className="py-6" aria-label="Equipment Log">
      <LoadingOverlay visible={loading && applied !== null} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <DateInput label="From" value={draft.fromDate} onChange={(value) => setDraft({ ...draft, fromDate: value ?? '' })} />
        <DateInput label="To" value={draft.toDate} onChange={(value) => setDraft({ ...draft, toDate: value ?? '' })} />
        <FormControlLabel
          control={<Checkbox checked={draft.includeAllClockReadings} onChange={(_, checked) => setDraft({ ...draft, includeAllClockReadings: checked })} />}
          label="Include all Clock Readings"
        />
        <Button variant="outlined" onClick={clear}>Clear</Button>
        <Button variant="contained" onClick={() => setApplied({ ...draft })} disabled={loading || invalidRange || !draft.fromDate || !draft.toDate}>Filter</Button>
      </div>
      {invalidRange && <Typography role="alert" color="error">End date must be on or after start date.</Typography>}
      {applied && error && (
        <div role="alert">
          <Typography color="error">{error.message}</Typography>
          <Button onClick={() => void refetch()}>Retry</Button>
        </div>
      )}
      {rows && rows.length === 0 && <Typography color="text.secondary">No results found.</Typography>}
      {rows && rows.length > 0 && (
        <DataGrid
          rowData={rows}
          columnDefs={columns}
          pagination
          paginationPageSize={20}
          paginationPageSizeSelector={[10, 20, 30, 40]}
          domLayout="autoHeight"
          height="auto"
        />
      )}
    </section>
  );
}